import type { TreatmentFormData } from "../treatmenttable/components/treatmentform/treatmentFormUtils";
import type { PrescriptionFormData } from "../prescription/prescriptionFormUtils";

/**
 * Unsaved rows for one patient visit, shared by every tab of the treatment
 * page. Tabs used to keep these in their own state, so switching tabs lost
 * them and the shell blocked the switch with an alert. Now the shell owns
 * them, mirrors them to sessionStorage, and saves them all at once.
 */

export type PlanCategory =
  | "Emergency"
  | "Preventive"
  | "Stabilization"
  | "Definitive";

export const PLAN_CATEGORIES: PlanCategory[] = [
  "Emergency",
  "Preventive",
  "Stabilization",
  "Definitive",
];

export interface PlanFormState {
  option: string;
  note: string;
}

export interface DraftRow<T> {
  id: string;
  data: T;
  /** Set when the last Save All rejected this row; cleared on next edit. */
  error?: string;
}

export interface VisitDrafts {
  treatments: DraftRow<TreatmentFormData>[];
  prescriptions: DraftRow<PrescriptionFormData>[];
  plan: Record<PlanCategory, PlanFormState>;
}

export interface DraftCounts {
  treatments: number;
  prescriptions: number;
  plan: number;
  total: number;
}

export const emptyPlanForms = (): Record<PlanCategory, PlanFormState> => ({
  Emergency: { option: "", note: "" },
  Preventive: { option: "", note: "" },
  Stabilization: { option: "", note: "" },
  Definitive: { option: "", note: "" },
});

export const emptyDrafts = (): VisitDrafts => ({
  treatments: [],
  prescriptions: [],
  plan: emptyPlanForms(),
});

export const createDraftId = () =>
  `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** A plan form counts as unsaved once something is typed or picked in it. */
export const isPlanFormPending = (form: PlanFormState | undefined) =>
  Boolean(form && (form.option.trim() || form.note.trim()));

export const countDrafts = (drafts: VisitDrafts): DraftCounts => {
  const plan = PLAN_CATEGORIES.filter((c) =>
    isPlanFormPending(drafts.plan[c])
  ).length;
  const treatments = drafts.treatments.length;
  const prescriptions = drafts.prescriptions.length;
  return {
    treatments,
    prescriptions,
    plan,
    total: treatments + prescriptions + plan,
  };
};

export const hasAnyDraft = (drafts: VisitDrafts) =>
  countDrafts(drafts).total > 0;

const STORAGE_PREFIX = "care32.visitDrafts";

export const draftStorageKey = (treatmentId: string, appointmentId: string) =>
  `${STORAGE_PREFIX}:${treatmentId}:${appointmentId || "-"}`;

const DATE_FIELDS = ["createdAt"] as const;

const reviveRow = <T extends Record<string, any>>(row: any): DraftRow<T> | null => {
  if (!row || typeof row !== "object" || typeof row.id !== "string") return null;
  if (!row.data || typeof row.data !== "object") return null;
  const data: Record<string, any> = { ...row.data };
  DATE_FIELDS.forEach((field) => {
    if (typeof data[field] === "string") {
      const parsed = new Date(data[field]);
      data[field] = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    }
  });
  return { id: row.id, data: data as T };
};

/** Parse a stored blob. Anything malformed falls back to no drafts. */
export const deserializeDrafts = (raw: string | null): VisitDrafts => {
  if (!raw) return emptyDrafts();
  try {
    const parsed = JSON.parse(raw);
    const plan = emptyPlanForms();
    PLAN_CATEGORIES.forEach((c) => {
      const stored = parsed?.plan?.[c];
      if (stored && typeof stored === "object") {
        plan[c] = {
          option: typeof stored.option === "string" ? stored.option : "",
          note: typeof stored.note === "string" ? stored.note : "",
        };
      }
    });
    return {
      treatments: Array.isArray(parsed?.treatments)
        ? parsed.treatments
            .map((row: any) => reviveRow<TreatmentFormData>(row))
            .filter(Boolean)
        : [],
      prescriptions: Array.isArray(parsed?.prescriptions)
        ? parsed.prescriptions
            .map((row: any) => reviveRow<PrescriptionFormData>(row))
            .filter(Boolean)
        : [],
      plan,
    };
  } catch {
    return emptyDrafts();
  }
};

/** Errors are transient and are not stored. */
export const serializeDrafts = (drafts: VisitDrafts): string =>
  JSON.stringify({
    treatments: drafts.treatments.map(({ id, data }) => ({ id, data })),
    prescriptions: drafts.prescriptions.map(({ id, data }) => ({ id, data })),
    plan: drafts.plan,
  });

const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

export const loadDrafts = (key: string): VisitDrafts => {
  const store = storage();
  if (!store) return emptyDrafts();
  try {
    return deserializeDrafts(store.getItem(key));
  } catch {
    return emptyDrafts();
  }
};

export const persistDrafts = (key: string, drafts: VisitDrafts) => {
  const store = storage();
  if (!store) return;
  try {
    if (hasAnyDraft(drafts)) {
      store.setItem(key, serializeDrafts(drafts));
    } else {
      store.removeItem(key);
    }
  } catch {
    // Quota or privacy mode: the in-memory copy still works for this page.
  }
};

/** Keep rows whose ids are listed as failed; drop the rest. */
export const dropSavedRows = <T>(
  rows: DraftRow<T>[],
  errors: Record<string, string>
): DraftRow<T>[] =>
  rows
    .filter((row) => row.id in errors)
    .map((row) => ({ ...row, error: errors[row.id] }));
